import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../../../lib/api/client"
import { mattersKeys } from "../../../lib/query-keys"

// Types for matter creation
export interface CreateMatterParty {
  contact: string // Contact ID
  workflow_contact: string // Workflow contact role ID
}

export interface CreateMatterRole {
  user: string // User ID
  workflow_role: string // Workflow role ID
}

export interface CreateMatterData {
  workflow: string // Workflow ID
  division: string // Division ID
  name: string // Matter name
  parties: CreateMatterParty[]
  roles: CreateMatterRole[]
}

/**
 * Hook to create a new matter.
 */
export function useCreateMatter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateMatterData) => api.matters.create(data),
    onSuccess: () => {
      // Invalidate the matters list to refetch
      queryClient.invalidateQueries({ queryKey: mattersKeys.lists() })
    },
  })
}

/**
 * Hook to fetch workflows for a division.
 */
export function useWorkflows(divisionId: string | null) {
  return useQuery({
    queryKey: ["workflows", { division: divisionId }],
    queryFn: async () => {
      const result = await api.workflows.search({
        filter: { division: divisionId, active: true },
      })
      // API returns { data: { documents: [...] } }
      return result.data?.documents || []
    },
    enabled: !!divisionId,
  })
}

/**
 * Hook to fetch workflow contacts (party roles) for a workflow.
 */
export function useWorkflowContacts(workflowId: string | null) {
  return useQuery({
    queryKey: ["workflowContacts", workflowId],
    queryFn: async () => {
      const result = await api.workflowContacts.find(workflowId!)
      return result.data || []
    },
    enabled: !!workflowId,
  })
}

/**
 * Hook to fetch workflow roles (staff roles) for a workflow.
 */
export function useWorkflowRoles(workflowId: string | null) {
  return useQuery({
    queryKey: ["workflowRoles", workflowId],
    queryFn: async () => {
      const result = await api.workflowRoles.find(workflowId!)
      return result.data || []
    },
    enabled: !!workflowId,
  })
}
